// Auto-generated pose presets - DO NOT EDIT
// Generated: 2026-02-01T15:23:48.512Z

import type { PosePreset } from '@/types';
import type { Gender } from '@/types/character';

// Female pose presets
export const FEMALE_POSE_PRESETS: PosePreset[] = [
  {
    id: 'standing-front',
    name: 'Standing Front',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/5341cb0e-aebb-4ae6-9fa4-6bf88f1ebfbb.png',
    prompt: `Re-pose the exact subject from the input image into an elegant fashion-model
      standing pose. Only the body position changes — identity must remain 1:1.

      FRAMING (HIGHEST PRIORITY): The output MUST show the complete figure from the top of the head to the soles of the feet, with visible margin above the head and below the feet. Never crop the head, hair, hands, knees, or feet. The full vertical length of the body must fit within the frame regardless of how the input was framed. If you cannot fit the full figure, zoom out — do not crop.

      POSE: Standing upright in a relaxed editorial stance with a slight hip shift (contrapposto) — weight on one leg, the other leg slightly relaxed with the knee soft. Shoulders level and relaxed, arms hanging naturally at the sides with hands loose. Torso facing forward or in a subtle three-quarter turn. Head upright, gaze toward the camera, calm neutral expression. Full body visible from head to toe, centered, with generous negative space above and below.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot, bust, or any cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. The output is ALWAYS a full-length head-to-toe portrait — the input's crop must not influence the output's framing.

      SCENE: Soft neutral light gray background, vertical full-length framing (portrait orientation, tall enough to fit the entire figure with margin), diffused studio lighting, gentle contact shadow at the feet, clean editorial fashion-photography aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.

      CRITICAL OUTPUT CHECK before finalizing: Are the top of the head AND both feet visible inside the frame, with margin? If not, re-frame wider. Cropped output is a failure case.
    `,
  },
  {
    id: 'standing-side',
    name: 'Standing Side',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/e8ecc2a4-15b1-4157-a122-f2fc72c3ddf2.png',
    prompt: `Re-pose the exact subject from the input image into a standing side-profile pose.
      Only the body position and viewing angle change — identity must remain 1:1.
      POSE: Standing upright in a strict side-profile view (body rotated 90° to the camera, showing the silhouette from the side). Weight evenly distributed, feet together or only slightly apart, legs straight in a relaxed stance. Arms hanging naturally at the sides, hands relaxed, shoulders neutral and level, posture upright with natural spinal curve. Head upright and facing forward in the direction the body is turned (profile of the face visible to camera), chin level, calm neutral expression. Full body visible from head to toe, centered, with generous negative space.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift. The profile view must clearly show the subject's recognizable side features (nose shape, jawline, ear, hairline) matching the input.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow at the feet, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement, body orientation, and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'hand-on-hip',
    name: 'Hand on Hip',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/61a8494f-dc95-4010-977a-57abaf331281.png',
    prompt: `Re-pose the exact subject from the input image into a standing hand-on-hip pose.
      Only the body position changes — identity must remain 1:1.
      POSE: Standing upright facing the camera, weight shifted slightly onto one leg in a soft contrapposto, the opposite leg relaxed with the knee softened. One hand placed firmly on the hip with the elbow bent outward and angled away from the body, fingers resting naturally along the hip line. The other arm hangs relaxed at the side, hand loose. Shoulders level and relaxed, posture confident and grounded, head upright facing forward, calm neutral expression. Full body visible from head to toe, centered, with generous negative space.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow at the feet, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'walking',
    name: 'Walking',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/ef5e49cf-5987-4489-9740-eff9f5aa2fe5.png',
    prompt: `Re-pose the exact subject from the input image into a confident mid-stride walking pose. Only the body position changes — identity must remain 1:1.
      FRAMING (HIGHEST PRIORITY): The output MUST show the complete figure from the top of the head to the soles of the feet, with visible margin above the head and below the feet. Never crop the head, hair, hands, knees, or feet.
      The full vertical length of the body must fit within the frame regardless of how the input was framed. If you cannot fit the full figure, zoom out — do not crop.
      POSE: Mid-stride walking to the right, captured in motion.
        - Legs: one leg striding forward with the knee softly bent and the foot
          landing heel-first; the opposite leg pushing off behind, knee straighter,
          heel lifted with the toe still in light contact with the ground.
        - Arms (contralateral swing — this is critical for a natural walk):
          the arm OPPOSITE the leading leg swings FORWARD, bent at the elbow
          around 90–110°, hand near waist or lower chest height, fingers relaxed.
          The arm on the SAME side as the leading leg swings BACK behind the hip,
          elbow softer, hand relaxed.
        - Torso: subtle counter-rotation against the hips (shoulders turn slightly
          opposite to the pelvis), upright posture, core engaged but relaxed.
        - Head: upright, gaze forward in the direction of travel, calm confident
          expression. Slight forward lean of the whole body suggesting momentum.
        - Body angled in a three-quarter view to the camera so the stride reads
          clearly. Full body visible from head to toe, centered, with generous
          negative space above and below.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift. Hair and any loose garments may show subtle motion consistent with the stride, but cut, color, and style must remain identical.
      PARTIAL INPUTS: If the input is a headshot, bust, or any cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments and simple footwear matching the input's palette and style. The output is ALWAYS a full-length head-to-toe portrait — the input's crop must not influence the output's framing.
      SCENE: Soft neutral light gray background, vertical full-length framing (portrait orientation, tall enough to fit the entire figure with margin), diffused studio lighting, gentle contact shadow under the grounded foot, clean editorial fashion-photography aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.
      CRITICAL OUTPUT CHECK before finalizing:
        1. Are the top of the head AND both feet visible inside the frame, with margin?
        2. Is the arm OPPOSITE the leading leg swinging forward (contralateral swing)?
      If either answer is no, re-frame and re-pose. Cropped or symmetric-arm output is a failure case.`,
  },
  {
    id: 'model-pose',
    name: 'Model Pose',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/8b5e280e-e333-4a02-a260-4fef9e3be98f.png',
    prompt: `Re-pose the exact subject from the input image into a neutral standing forward-facing pose. Only the body position changes — identity must remain 1:1.
      POSE: Standing upright facing the camera straight on, weight evenly distributed with feet together or only slightly apart, legs straight in a relaxed neutral stance. Both arms hanging naturally and symmetrically at the sides, hands relaxed with fingers softly extended, palms facing inward toward the thighs. Shoulders level and relaxed, chest open, posture upright with natural spinal alignment, head upright facing directly forward, chin level, calm neutral expression. Full body visible from head to toe, centered, with generous
      negative space.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow at the feet, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'twirl',
    name: 'Twirl',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/6ec3838a-3b35-48fe-ad65-0e847195a69c.png',
    prompt: `Re-pose the exact subject from the input image into a graceful twirling dance pose.
      Only the body position changes — identity must remain 1:1.
      POSE: Standing on one supporting leg, the other leg lifted and bent at the knee with the foot pointing down behind, in a ballet-inspired attitude / passé position. Arms extended outward in a flowing asymmetric line — one arm reaching up and out overhead, the other arm extended down and out to the opposite side, creating a sweeping diagonal across the body. Torso gently curved with a soft twist, suggesting motion mid-turn. Head turned naturally toward the raised arm, expression light and serene. Full body visible from head to toe, centered, with generous negative space to convey movement.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift. Hair and any flowing garments may show subtle motion consistent with the twirl, but cut, color, and style must remain identical.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow under the supporting foot, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'sitting',
    name: 'Sitting',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/ef5d5b9e-d0ab-475a-a6b2-1d631470161e.png',
    prompt: `Re-pose the exact subject from the input image into a seated cross-legged pose.
      Only the body position changes — identity must remain 1:1.
      POSE: Seated on the ground, one knee drawn up toward the chest, the opposite leg folded underneath. One forearm resting on the raised knee, the other hand resting softly on the shin, ankle, or lap. Torso in a relaxed three-quarter view, slight forward lean, head upright and natural. Full body visible from head to toe, centered, with generous negative space.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'arms-crossed',
    name: 'Arms Crossed',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/19e904d4-a240-423d-abee-44e94547d34b.png',
    prompt: `Re-pose the exact subject from the input image into a standing arms-crossed pose.
      Only the body position changes — identity must remain 1:1.
      POSE: Standing upright facing the camera, weight evenly distributed with feet roughly shoulder-width apart (or close together for a more compact silhouette), legs straight in a relaxed stance. Arms folded across the chest, one forearm resting over the other, hands tucked naturally against the upper arms.
      Shoulders back, posture confident and grounded, head upright facing forward, calm neutral expression. Full body visible from head to toe, centered, with generous negative space.
      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color, hair color/length/style, body build, clothing, accessories, makeup, and expression as the input. Photorealistic — no stylization, beautification, or age/gender/ethnicity shift.
      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of the body consistent with visible cues (skin tone, build, styling). Extend the visible clothing naturally; where none is visible, add minimal neutral-toned garments matching the input's palette and style. Always render the complete figure head-to-toe — never crop.
      SCENE: Soft neutral light gray background, diffused studio lighting, gentle contact shadow at the feet, clean editorial aesthetic, no props or text.
      POSE REFERENCE: The figurine reference is a pose guide only — match limb placement and silhouette, NOT its matte black material or featureless style.`,
  },
];

// Male pose presets
export const MALE_POSE_PRESETS: PosePreset[] = [
  {
    id: 'walking',
    name: 'Walking',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/8f4048be-914a-440c-a910-cc892209587a.png',
    prompt: `Re-pose the exact subject from the input image into a confident mid-stride
        walking pose. Only the body position changes — identity must remain 1:1.
        FRAMING (HIGHEST PRIORITY): The output MUST show the complete figure from
        the top of the head to the soles of the feet, with visible margin above the
        head and below the feet. Never crop the head, hair, hands, knees, or feet.
        The full vertical length of the body must fit within the frame regardless of
        how the input was framed. If you cannot fit the full figure, zoom out — do
        not crop.
        POSE: Mid-stride walking to the right, captured in motion.
          - Legs: one leg striding forward with the knee softly bent and the foot
            landing heel-first; the opposite leg pushing off behind, knee straighter,
            heel lifted with the toe still in light contact with the ground.
          - Arms (contralateral swing — this is critical for a natural walk):
            the arm OPPOSITE the leading leg swings FORWARD, bent at the elbow
            around 90–110°, hand near waist or lower chest height, fingers relaxed.
            The arm on the SAME side as the leading leg swings BACK behind the hip,
            elbow softer, hand relaxed.
          - Torso: subtle counter-rotation against the hips (shoulders turn slightly
            opposite to the pelvis), upright posture, core engaged but relaxed.
          - Head: upright, gaze forward in the direction of travel, calm confident
            expression. Slight forward lean of the whole body suggesting momentum.
          - Body angled in a three-quarter view to the camera so the stride reads
            clearly. Full body visible from head to toe, centered, with generous
            negative space above and below.
        IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
        hair color/length/style, body build, clothing, accessories, makeup, and
        expression as the input. Photorealistic — no stylization, beautification,
        or age/gender/ethnicity shift. Hair and any loose garments may show subtle
        motion consistent with the stride, but cut, color, and style must remain
        identical.
        PARTIAL INPUTS: If the input is a headshot, bust, or any cropped view, infer
        the rest of the body consistent with visible cues (skin tone, build, styling).
        Extend the visible clothing naturally; where none is visible, add minimal
        neutral-toned garments and simple footwear matching the input's palette and
        style. The output is ALWAYS a full-length head-to-toe portrait — the input's
        crop must not influence the output's framing.
        SCENE: Soft neutral light gray background, vertical full-length framing
        (portrait orientation, tall enough to fit the entire figure with margin),
        diffused studio lighting, gentle contact shadow under the grounded foot,
        clean editorial fashion-photography aesthetic, no props or text.
        POSE REFERENCE: The figurine reference is a pose guide only — match limb
        placement and silhouette, NOT its matte black material or featureless style.
        CRITICAL OUTPUT CHECK before finalizing:
          1. Are the top of the head AND both feet visible inside the frame, with margin?
          2. Is the arm OPPOSITE the leading leg swinging forward (contralateral swing)?
        If either answer is no, re-frame and re-pose. Cropped or symmetric-arm output
        is a failure case.`,
  },
  {
    id: 'casual-lean',
    name: 'Casual Lean',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/dd52344f-6e58-436e-9400-a6e2482f9aea.png',
    prompt: `Re-pose the exact subject from the input image into a casual leaning standing pose.
      Only the body position changes — identity must remain 1:1.

      POSE: Standing in a relaxed leaning stance as if casually resting against an
      unseen wall behind. Weight shifted firmly onto one straight supporting leg,
      the opposite leg crossed loosely in front at the ankle or angled across the
      supporting leg, foot resting lightly on its toe or outer edge. Hips tilted to
      one side, torso leaning slightly in the same direction, shoulders relaxed and
      slightly uneven following the lean. Both arms hanging loose at the sides,
      hands relaxed — the arm on the supporting-leg side may rest naturally along
      the hip or thigh. Head tilted very slightly, gaze forward or softly to the
      side, calm easygoing expression. Full body visible from head to toe, centered,
      with generous negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'arms-crossed',
    name: 'Arms Crossed',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/8942fd89-1982-457c-bef8-e7667914aa34.png',
    prompt: `Re-pose the exact subject from the input image into a standing arms-crossed pose.
      Only the body position changes — identity must remain 1:1.

      POSE: Standing upright facing the camera, weight evenly distributed with feet
      roughly shoulder-width apart (or close together for a more compact silhouette),
      legs straight in a relaxed stance. Arms folded across the chest, one forearm
      resting over the other, hands tucked naturally against the upper arms.
      Shoulders back, posture confident and grounded, head upright facing forward,
      calm neutral expression. Full body visible from head to toe, centered, with
      generous negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'standing-front',
    name: 'Standing Front',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/cb6c66f0-7842-4d97-b3f3-7c724da1aee0.png',
    prompt: `Re-pose the exact subject from the input image into a neutral standing
      forward-facing pose. Only the body position changes — identity must remain 1:1.

      POSE: Standing upright facing the camera straight on, weight evenly distributed
      with feet together or only slightly apart, legs straight in a relaxed neutral
      stance. Both arms hanging naturally and symmetrically at the sides, hands
      relaxed with fingers softly extended, palms facing inward toward the thighs.
      Shoulders level and relaxed, chest open, posture upright with natural spinal
      alignment, head upright facing directly forward, chin level, calm neutral
      expression. Full body visible from head to toe, centered, with generous
      negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'power-stance',
    name: 'Power Stance',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/cf77cee6-1b45-4438-9982-4b92e3f6b53f.png',
    prompt: `Re-pose the exact subject from the input image into a wide-stance hands-on-hips
      power pose. Only the body position changes — identity must remain 1:1.

      POSE: Standing upright facing the camera with a wide, grounded stance — feet
      planted firmly apart, noticeably wider than shoulder-width, legs straight and
      weight evenly distributed between both legs. Both hands placed firmly on the
      hips with thumbs pointing forward or back and fingers wrapped along the hip
      line, both elbows bent and angled outward away from the body forming clear
      triangular shapes on each side. Chest open, shoulders back and level, posture
      strong and confident, head upright facing directly forward, chin level, calm
      assured expression. Full body visible from head to toe, centered, with
      generous negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'standing-side',
    name: 'Standing Side',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/2500016e-e26d-427b-8890-b8ec3d3d6b0a.png',
    prompt: `Re-pose the exact subject from the input image into a standing side-profile pose.
      Only the body position and viewing angle change — identity must remain 1:1.

      POSE: Standing upright in a strict side-profile view (body rotated 90° to the
      camera, showing the silhouette from the side). Weight evenly distributed,
      feet together or only slightly apart, legs straight in a relaxed stance.
      Arms hanging naturally at the sides, hands relaxed, shoulders neutral and
      level, posture upright with natural spinal curve. Head upright and facing
      forward in the direction the body is turned (profile of the face visible to
      camera), chin level, calm neutral expression. Full body visible from head to
      toe, centered, with generous negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift. The profile view must clearly show the
      subject's recognizable side features (nose shape, jawline, ear, hairline)
      matching the input.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement, body orientation, and silhouette, NOT its matte black material or
      featureless style.`,
  },
  {
    id: 'sitting',
    name: 'Sitting',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/0561681f-8b61-4af9-b245-b317a49984ee.png',
    prompt: `Re-pose the exact subject from the input image into a relaxed seated
      ground pose. Only the body position changes — identity must remain 1:1.

      POSE: Seated on the ground in a casual upright position. One knee drawn up
      with the foot planted flat on the ground close to the body, the thigh roughly
      vertical. The opposite leg folded comfortably in front, knee bent outward to
      the side and lower leg resting along the ground. One forearm rests easily
      across the top of the raised knee, hand relaxed and dangling loosely. The
      other hand rests softly on the folded leg, the ankle, or lightly on the
      ground beside the hip for support. Torso upright with a gentle natural
      forward lean, shoulders relaxed, head upright facing forward, calm composed
      expression. Full body visible from head to toe, centered, with generous
      negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow under the seated figure, clean editorial aesthetic, no props
      or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
  {
    id: 'hands-pockets',
    name: 'Hands in Pockets',
    thumbnailUrl: 'https://cdn-cms-uploads.picsart.com/cms-uploads/ce41a4a4-5672-4582-a3fe-8c1070a0cd31.png',
    prompt: `Re-pose the exact subject from the input image into a standing hand-on-hip pose.
      Only the body position changes — identity must remain 1:1.

      POSE: Standing upright facing the camera, weight shifted slightly onto one leg
      in a soft contrapposto, the opposite leg relaxed with the knee softened. One
      hand placed firmly on the hip with the elbow bent outward and angled away
      from the body, fingers resting naturally along the hip line. The other arm
      hangs relaxed at the side, hand loose. Shoulders level and relaxed, posture
      confident and grounded, head upright facing forward, calm neutral expression.
      Full body visible from head to toe, centered, with generous negative space.

      IDENTITY (preserve exactly): same face, features, skin tone, marks, eye color,
      hair color/length/style, body build, clothing, accessories, makeup, and
      expression as the input. Photorealistic — no stylization, beautification,
      or age/gender/ethnicity shift.

      PARTIAL INPUTS: If the input is a headshot or cropped view, infer the rest of
      the body consistent with visible cues (skin tone, build, styling). Extend the
      visible clothing naturally; where none is visible, add minimal neutral-toned
      garments matching the input's palette and style. Always render the complete
      figure head-to-toe — never crop.

      SCENE: Soft neutral light gray background, diffused studio lighting, gentle
      contact shadow at the feet, clean editorial aesthetic, no props or text.

      POSE REFERENCE: The figurine reference is a pose guide only — match limb
      placement and silhouette, NOT its matte black material or featureless style.`,
  },
];

// Get pose presets by gender
export function getPosePresetsByGender(gender: Gender): PosePreset[] {
  switch (gender) {
    case 'female':
      return FEMALE_POSE_PRESETS;
    case 'male':
      return MALE_POSE_PRESETS;
    default:
      return FEMALE_POSE_PRESETS;
  }
}

// Legacy export (defaults to female)
export const GENERATED_POSE_PRESETS = FEMALE_POSE_PRESETS;
